import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, CheckCircle2, FileText, Folder, Globe, Server } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const DeploymentHelper = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleDownload = () => {
    toast({
      title: 'Download Starting...',
      description: 'Your deployment package is being downloaded.',
    });
    
    // Trigger download
    window.location.href = `${BACKEND_URL}/api/download-deployment`;
  };

  const steps = [
    {
      number: 1,
      title: 'Build Production Files',
      description: 'Run "yarn build" in the frontend directory to create optimized production files',
      icon: Folder,
      command: 'cd /app/frontend && yarn build'
    },
    {
      number: 2,
      title: 'Download Deployment Package',
      description: 'Download the deployment package zip file from the server',
      icon: Download,
      path: '/app/sanghvi-website-cpanel-deployment.zip'
    },
    {
      number: 3,
      title: 'Upload to cPanel',
      description: 'Login to cPanel, open File Manager, navigate to public_html and upload files',
      icon: Server,
    },
    {
      number: 4,
      title: 'Extract & Configure',
      description: 'Extract the zip file and ensure .htaccess is in place',
      icon: FileText,
    },
    {
      number: 5,
      title: 'Go Live',
      description: 'Visit your domain to see the website live',
      icon: Globe,
    }
  ];

  const files = [
    { name: 'index.html', size: '~3 KB', required: true },
    { name: 'logo.png', size: '~4 KB', required: true },
    { name: '.htaccess', size: '~2 KB', required: true },
    { name: 'static/css/', size: '~13 KB', required: true },
    { name: 'static/js/', size: '~116 KB', required: true },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">
            🚀 cPanel Deployment Helper
          </h1>
          <p className="text-xl text-gray-600">
            Easy guide to deploy Sanghvi Infotech website to your cPanel hosting
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 justify-center">
          <Button
            onClick={() => setActiveTab('overview')}
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            className="px-6"
          >
            Overview
          </Button>
          <Button
            onClick={() => setActiveTab('steps')}
            variant={activeTab === 'steps' ? 'default' : 'outline'}
            className="px-6"
          >
            Step-by-Step
          </Button>
          <Button
            onClick={() => setActiveTab('files')}
            variant={activeTab === 'files' ? 'default' : 'outline'}
            className="px-6"
          >
            File List
          </Button>
          <Button
            onClick={() => setActiveTab('download')}
            variant={activeTab === 'download' ? 'default' : 'outline'}
            className="px-6"
          >
            Download
          </Button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <Card className="border-none shadow-xl">
              <CardHeader className="bg-blue-600 text-white">
                <CardTitle className="text-2xl">Quick Overview</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-3xl font-bold text-green-600 mb-2">5 min</div>
                    <div className="text-gray-600">Deployment Time</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-3xl font-bold text-blue-600 mb-2">584 KB</div>
                    <div className="text-gray-600">Package Size</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-3xl font-bold text-purple-600 mb-2">100%</div>
                    <div className="text-gray-600">Production Ready</div>
                  </div>
                </div>

                <div className="mt-8 space-y-4">
                  <h3 className="text-xl font-bold text-gray-900">What's Included:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Complete website with all pages (Home, About, Services, Blog, Contact)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Optimized production build (minified & compressed)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Logo and all images integrated</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>.htaccess configured for React Router, Gzip, and caching</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Mobile responsive & SEO optimized</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Page transitions and animations</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Steps Tab */}
        {activeTab === 'steps' && (
          <div className="space-y-6">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold">
                          {step.number}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-center gap-2 mb-2">
                          <IconComponent className="h-5 w-5 text-blue-600" />
                          <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                        </div>
                        <p className="text-gray-600 mb-3">{step.description}</p>
                        {step.command && (
                          <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-sm">
                            {step.command}
                          </div>
                        )}
                        {step.path && (
                          <div className="bg-blue-50 text-blue-900 p-3 rounded font-mono text-sm">
                            📦 {step.path}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Files Tab */}
        {activeTab === 'files' && (
          <Card className="border-none shadow-xl">
            <CardHeader className="bg-blue-600 text-white">
              <CardTitle className="text-2xl">Files to Upload to cPanel</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-3">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                    <div className="flex items-center gap-3">
                      <Folder className="h-5 w-5 text-blue-600" />
                      <div>
                        <div className="font-semibold text-gray-900">{file.name}</div>
                        <div className="text-sm text-gray-500">{file.size}</div>
                      </div>
                    </div>
                    {file.required && (
                      <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Required</span>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                <p className="text-yellow-800">
                  <strong>Note:</strong> All files must be uploaded to your cPanel's <code className="bg-yellow-200 px-2 py-1 rounded">public_html</code> directory (or your domain's document root).
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Download Tab */}
        {activeTab === 'download' && (
          <div className="space-y-6">
            <Card className="border-none shadow-xl">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <CardTitle className="text-2xl">Download Deployment Package</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="text-center space-y-6">
                  <div className="text-6xl mb-4">📦</div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    sanghvi-website-cpanel-deployment.zip
                  </h3>
                  <p className="text-gray-600 text-lg">
                    Contains all files needed for cPanel deployment
                  </p>
                  
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <h4 className="font-bold text-gray-900 mb-4">Package Location on Server:</h4>
                    <div className="bg-white p-4 rounded border-2 border-blue-200 font-mono text-sm break-all">
                      /app/sanghvi-website-cpanel-deployment.zip
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Button 
                      size="lg" 
                      className="w-full md:w-auto px-8 py-6 text-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                      onClick={handleDownload}
                    >
                      <Download className="mr-2 h-5 w-5" />
                      Download Deployment Package
                    </Button>
                    <p className="text-sm text-gray-500">Click to download sanghvi-website-cpanel-deployment.zip (596 KB)</p>
                  </div>

                  <div className="text-left space-y-4 mt-8 p-6 bg-gray-50 rounded-lg">
                    <h4 className="font-bold text-gray-900 text-lg mb-4">Alternative Download Methods:</h4>
                    
                    <div className="space-y-3">
                      <div className="border-l-4 border-blue-500 pl-4">
                        <div className="font-semibold text-gray-900">Method 1: Direct Server Access</div>
                        <p className="text-sm text-gray-600">Access your server via SSH/terminal and copy the file from /app/</p>
                        <code className="block bg-gray-900 text-green-400 p-2 rounded text-xs mt-2">
                          scp root@your-server:/app/sanghvi-website-cpanel-deployment.zip .
                        </code>
                      </div>

                      <div className="border-l-4 border-green-500 pl-4">
                        <div className="font-semibold text-gray-900">Method 2: Use File Manager</div>
                        <p className="text-sm text-gray-600">If you have file manager access, navigate to /app/ and download the zip file</p>
                      </div>

                      <div className="border-l-4 border-purple-500 pl-4">
                        <div className="font-semibold text-gray-900">Method 3: Rebuild Locally</div>
                        <p className="text-sm text-gray-600">Clone the repository and run the build command:</p>
                        <code className="block bg-gray-900 text-green-400 p-2 rounded text-xs mt-2">
                          cd /app/frontend && yarn build
                        </code>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Documentation Cards */}
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-none shadow-lg hover:shadow-xl transition">
                <CardHeader className="bg-green-100">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5 text-green-600" />
                    Quick Start
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-600 mb-3">5-minute deployment guide with screenshots</p>
                  <p className="text-xs font-mono bg-gray-100 p-2 rounded">QUICK_START_CPANEL.md</p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg hover:shadow-xl transition">
                <CardHeader className="bg-blue-100">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    Full Guide
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-600 mb-3">Complete deployment instructions with troubleshooting</p>
                  <p className="text-xs font-mono bg-gray-100 p-2 rounded">DEPLOYMENT_GUIDE.md</p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg hover:shadow-xl transition">
                <CardHeader className="bg-purple-100">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5 text-purple-600" />
                    README
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-600 mb-3">Inside the deployment package</p>
                  <p className="text-xs font-mono bg-gray-100 p-2 rounded">README_DEPLOYMENT.txt</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Quick Links */}
        <Card className="border-none shadow-xl mt-8">
          <CardHeader className="bg-gray-800 text-white">
            <CardTitle>Need Help?</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Common Issues:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Blank page → Check .htaccess file</li>
                  <li>• 404 errors → Enable mod_rewrite in cPanel</li>
                  <li>• Images not loading → Verify file permissions (644)</li>
                  <li>• Styles broken → Clear browser cache</li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Requirements:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• cPanel hosting account</li>
                  <li>• File Manager or FTP access</li>
                  <li>• Domain configured</li>
                  <li>• ~1 MB free space</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DeploymentHelper;
